/**
 * МОЖЕТ ЛИ АККАУНТ СЕЙЧАС ПРИНЯТЬ ДОКУМЕНТ — и надо ли держать копию в браузере (SCRUM-504, 363).
 *
 * Владелец, 13.10.2026: «в смысле так это нормально что он должен подтвердить почту» —
 * подтверждение почты НОРМА и остаётся; «ненормально если работа теряется или падает», «чтобы не
 * терялся райдер».
 *
 * ЧТО БЫЛО ИЗМЕРЕНО. Черновик в браузер писался с условием «не вошёл»
 * (`editor-page.ts`: `if (!isLoggedIn()) writeGuestDraft(...)`), с резонным доводом: у вошедшего
 * документ принадлежит аккаунту, а вторая копия в браузере — залежавшаяся. Но тот, кто ТОЛЬКО ЧТО
 * ЗАРЕГИСТРИРОВАЛСЯ и ещё не нажал в письме, уже «вошёл»: сервер его запись отклоняет
 * (`VerifiedEmailGuard`, 403 `email_not_verified`, на деве `REQUIRE_VERIFIED_EMAIL=true`), а копии
 * в браузере ему не делают. Всё, что он построит после отказа, живёт только в памяти вкладки —
 * открыл почту в той же вкладке, и работы нет.
 *
 * ПОЧЕМУ СОСТОЯНИЙ ТРИ, А НЕ ДВА (находка критика 2, 14.10.2026). Первая правка этого модуля
 * держала один флаг «отказ был», и он отвечал «аккаунт рабочий» в двух РАЗНЫХ случаях: когда запись
 * прошла — и когда записи не было вовсе. Второй случай — это ровно новичок, который ещё ничего не
 * сохранял: час работы ему копии не давал, потому что отказа «ещё не было». И флаг жил в памяти
 * модуля, то есть перезагрузка страницы возвращала «аккаунт рабочий» даже тому, кому сервер уже
 * отказал. Поэтому состояние называется прямо: `unknown` (не пробовали), `refuses` (сервер отказал),
 * `accepts` (сервер принял) — и копию НЕ держат только в `accepts`.
 *
 * ПОЧЕМУ ОНО ПРИВЯЗАНО К АККАУНТУ И ЛЕЖИТ В `localStorage`. «Сервер принял» — это факт про ОДИН
 * аккаунт: вышел, зарегистрировался другой человек в том же браузере — и чужое `accepts` лишило бы
 * его копии ровно в самом опасном месте. Поэтому в хранилище лежит пара «аккаунт + состояние», и
 * состояние чужого аккаунта читается как `unknown`. Хранилище — `localStorage`, а не память модуля:
 * письмо с подтверждением открывается в НОВОЙ вкладке (то же основание, что у `offer-auto-send.ts`),
 * и перезагрузка не должна возвращать гейт в «не пробовали».
 */
import { getStoredUser, isEmailNotVerifiedError } from "./http";

/** Что известно про записи этого аккаунта: не пробовали · сервер отказал · сервер принял. */
export type AccountWrites = "unknown" | "refuses" | "accepts";

const STORAGE_KEY = "scb.accountWrites";

/** Запасная память, когда `localStorage` недоступен (приватное окно, запрет на данные сайта):
 *  гейт обязан работать и там — хуже только тем, что не переживёт перезагрузку. */
let fallback: { account: string; state: AccountWrites } | null = null;

/** Пустая строка — гость: у него аккаунта нет, и состояние ему не принадлежит. */
function currentAccount(): string {
  try {
    return getStoredUser()?.id ?? "";
  } catch {
    return "";
  }
}

function remember(state: AccountWrites): void {
  const account = currentAccount();
  fallback = { account, state };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ account, state }));
  } catch {
    /* хранилище недоступно — состояние осталось в памяти модуля на эту вкладку. */
  }
}

/**
 * Состояние записей ТЕКУЩЕГО аккаунта. Записанное под другим аккаунтом — не наше: читается как
 * `unknown`, то есть копию в браузере держат. Мусор в хранилище — тоже `unknown`: гейт не падает
 * из-за испорченной строки, он становится осторожнее.
 */
export function accountWrites(): AccountWrites {
  const account = currentAccount();
  let stored: unknown = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    stored = raw ? JSON.parse(raw) : null;
  } catch {
    stored = null;
  }
  const record =
    stored && typeof stored === "object" ? (stored as { account?: unknown; state?: unknown }) : fallback;
  if (!record || typeof record.account !== "string" || record.account !== account) return "unknown";
  return record.state === "refuses" || record.state === "accepts" ? record.state : "unknown";
}

/**
 * Запись отклонена аккаунтом? Возвращает `true`, если это был отказ «почта не подтверждена», и
 * запоминает его: дальше копия в браузере держится, пока аккаунт не примет документ.
 */
export function noteWriteRefusal(err: unknown): boolean {
  if (!isEmailNotVerifiedError(err)) return false;
  remember("refuses");
  return true;
}

/** Аккаунт принял документ. Зовётся после УСПЕШНОЙ записи, не раньше: это единственное, что
 *  позволяет перестать держать копию в браузере. */
export function noteWriteAccepted(): void {
  remember("accepts");
}

/** Известно, что аккаунт сейчас отклоняет записи (почта не подтверждена). */
export function accountRefusesWrites(): boolean {
  return accountWrites() === "refuses";
}

/**
 * Держать ли копию документа в браузере. Правило одно: копию НЕ держат только тогда, когда сервер
 * этого аккаунта уже ПРИНЯЛ документ. Все остальные случаи — про одно: документу некуда деться.
 *  · гость — аккаунта нет вовсе (правило SCRUM-300: черновик живёт в сессии и передаётся при входе);
 *  · вошедший, чьи записи отклонены — подтверждение почты ещё впереди;
 *  · вошедший, который ещё ничего не сохранял — про его аккаунт НИЧЕГО не известно, и ошибка здесь
 *    стоит часа работы, а лишняя копия не стоит ничего.
 */
export function shouldKeepLocalCopy(state: { loggedIn: boolean; accountWrites: AccountWrites }): boolean {
  return !state.loggedIn || state.accountWrites !== "accepts";
}

/** Только для тестов: вернуть модуль и хранилище в исходное состояние. */
export function resetWriteGateForTests(): void {
  fallback = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* нечего чистить */
  }
}
